/**
 * @fileoverview Recovery from failed dynamic-import (chunk) loads.
 *
 * Hosting serves one version at a time, and each build content-hashes its
 * chunk filenames. A tab that loaded before a release still holds the old
 * module graph, so the first `import()` it makes afterwards asks for a
 * filename the live version no longer has; loads also fail transiently on a
 * poor connection. Vite reports either as `vite:preloadError` (see
 * https://vite.dev/guide/build.html#load-error-handling). The recovery is to
 * reload, because stable browsers cache a failed module fetch for the life of
 * the document, so retrying the `import()` in place never refetches.
 *
 * Installed from the shared `bootstrapCipApp`, which both entrypoints call,
 * rather than written out in each — the drift that left PAI without a guard at
 * all ([#3696](https://github.com/CodeinPlace/cip/issues/3696)).
 */

import {BUILD_COMMIT} from 'utils/general';
import {z} from 'zod';
import {reloadPage} from 'appReload';

/**
 * sessionStorage key holding the build and path the tab last reloaded to
 * recover. Values written by earlier implementations (`'true'`, or a bare
 * build SHA) never match the current format, so they read as "no reload
 * recorded" and cannot wedge an already-open tab.
 */
const RELOAD_MARKER_KEY = 'cip_chunk_reload';

/**
 * {@link BUILD_COMMIT} of a build that could not read its own commit; see
 * `buildCommitMeta.ts`.
 */
const UNKNOWN_BUILD_COMMIT = 'unknown';

/** sessionStorage key written and removed to test that storage works. */
const RELOAD_MARKER_PROBE_KEY = 'cip_chunk_reload_probe';

/**
 * sessionStorage key holding the recovery reload this tab requested and has
 * not yet reported. Separate from {@link RELOAD_MARKER_KEY}, which must
 * outlive the report to keep bounding reloads.
 */
const RELOAD_REPORT_KEY = 'cip_chunk_reload_report';

/**
 * Longest failure message kept in the reload report. Browser messages name
 * the chunk's URL, so this keeps the URL while bounding what an unexpected
 * message can add to the log entry.
 */
const MAX_REPORTED_ERROR_LENGTH = 500;

/**
 * A recovery reload as recorded before the page unloads, for the next load to
 * report. The previous document cannot report it: its logging call would be
 * cancelled by the unload it is about to start.
 */
const ReloadReportSchema = z.object({
  /** Build the tab was running when the chunk failed. */
  fromBuild: z.string(),
  /** Pathname of the page whose chunk failed. */
  path: z.string(),
  /**
   * The failure's message, which names the chunk's URL outside Safari.
   * Empty when the failure was not an `Error`.
   */
  error: z.string(),
  /** When the reload was requested, as an ISO 8601 string. */
  reloadedAt: z.string(),
});

/** See {@link ReloadReportSchema}. */
type ReloadReport = z.infer<typeof ReloadReportSchema>;

/**
 * The failure messages of WebKit, Chromium and Firefox for a module that did
 * not load, Vite's for a CSS dependency that did not, and WebKit's for a
 * chunk URL answered with HTML.
 */
const CHUNK_LOAD_ERROR_PATTERN =
  /Importing a module script failed|Failed to fetch dynamically imported module|error loading dynamically imported module|Unable to preload CSS|is not a valid JavaScript MIME type/;

/**
 * How long a reload the browser asked the user to confirm may stay pending
 * before it is taken as declined.
 */
const CONFIRMED_RELOAD_GRACE_MS = 5_000;

let isReloadPending = false;
const reloadPendingListeners = new Set<() => void>();

/**
 * The failure this document last requested a recovery reload for. Never
 * cleared: a declined reload leaves the failed import unresolved for the life
 * of the document.
 */
let unrecoveredChunkLoadError: Error | undefined;

function setReloadPending(isPending: boolean): void {
  isReloadPending = isPending;
  reloadPendingListeners.forEach(listener => listener());
}

function readReloadMarker(): string | null {
  try {
    return sessionStorage.getItem(RELOAD_MARKER_KEY);
  } catch (e) {
    console.debug('[staleChunkGuard] reading the reload marker failed', e);
    return null;
  }
}

/**
 * Tells whether an error is a failed chunk load.
 *
 * @param error - Anything thrown, e.g. from React Router's `useRouteError`.
 * @returns True when the error's message is one a browser or Vite uses for a
 *   chunk that did not load.
 * @throws Never.
 */
export function isChunkLoadError(error: unknown): boolean {
  const message =
    typeof error === 'object' && error !== null && 'message' in error
      ? String(error.message)
      : String(error);
  return CHUNK_LOAD_ERROR_PATTERN.test(message);
}

/**
 * Decides whether a failed chunk load should trigger a recovery reload, and
 * records the decision.
 *
 * The rule is one reload per build per path: a reload is allowed unless this
 * tab's last recovery reload was for this same build and path. That
 * terminates — the reload lands on the same path, so a persistently-broken
 * chunk fails once more and then stops — while rearming when a release
 * changes the build or the user moves to another page. A budget of one per
 * tab left an intermittent connection with no recovery for the rest of the
 * session ([#4503](https://github.com/CodeinPlace/cip/issues/4503)).
 *
 * Keying on build and path rather than elapsed time is deliberate. A guard
 * that re-armed on every `load` event reloaded a broken chunk forever
 * ([#3598](https://github.com/CodeinPlace/cip/issues/3598)), and a timeout
 * would only have slowed that loop down, not ended it.
 *
 * @param params.buildCommit - Identifier of the build this tab is running.
 *   Must change whenever chunk filenames can change; see {@link BUILD_COMMIT}.
 * @param params.path - Pathname of the page whose chunk failed.
 * @returns True when the caller should reload. False when the decision could
 *   not be recorded, since an unrecorded reload could repeat without end.
 * @throws Never — a `sessionStorage` failure (private mode, blocked site data)
 *   is logged at debug level.
 */
export function shouldReloadForStaleChunk({
  buildCommit,
  path,
}: {
  buildCommit: string;
  path: string;
}): boolean {
  const marker = `${buildCommit} ${path}`;
  if (readReloadMarker() === marker) {
    return false;
  }

  try {
    sessionStorage.setItem(RELOAD_MARKER_KEY, marker);
  } catch (e) {
    // Nothing else bounds the reloads: without the marker a persistently
    // broken chunk reloads the tab forever.
    console.debug('[staleChunkGuard] writing the reload marker failed', e);
    return false;
  }
  return true;
}

function writeReloadReport(report: ReloadReport): void {
  try {
    sessionStorage.setItem(RELOAD_REPORT_KEY, JSON.stringify(report));
  } catch (e) {
    console.debug('[staleChunkGuard] writing the reload report failed', e);
  }
}

function removeReloadReport(): void {
  try {
    sessionStorage.removeItem(RELOAD_REPORT_KEY);
  } catch (e) {
    console.debug('[staleChunkGuard] removing the reload report failed', e);
  }
}

/**
 * Reads and removes the pending reload report, so that it is reported at most
 * once whatever happens after.
 */
function takeReloadReport(): ReloadReport | undefined {
  let stored: string | null;
  try {
    stored = sessionStorage.getItem(RELOAD_REPORT_KEY);
  } catch (e) {
    console.debug('[staleChunkGuard] reading the reload report failed', e);
    return undefined;
  }
  if (stored === null) {
    return undefined;
  }
  removeReloadReport();
  let parsed: unknown;
  try {
    parsed = JSON.parse(stored);
  } catch (e) {
    console.debug('[staleChunkGuard] the reload report is not JSON', e);
    return undefined;
  }
  const result = ReloadReportSchema.safeParse(parsed);
  return result.success ? result.data : undefined;
}

/**
 * Tells whether the reload marker can be stored, without recording a reload.
 */
function canStoreReloadMarker(): boolean {
  try {
    sessionStorage.setItem(RELOAD_MARKER_PROBE_KEY, '1');
    sessionStorage.removeItem(RELOAD_MARKER_PROBE_KEY);
    return true;
  } catch (e) {
    console.debug('[staleChunkGuard] the reload marker cannot be stored', e);
    return false;
  }
}

/**
 * Reloads the page to recover from a failed chunk load, at most once per
 * build and path (see {@link shouldReloadForStaleChunk}).
 *
 * @param params.buildCommit - Build identifier, for tests. Omitted →
 *   {@link BUILD_COMMIT}. A build that does not stamp one reads as
 *   `'unknown'`, which never changes across releases.
 * @param params.path - Pathname, for tests. Omitted → the current
 *   `window.location.pathname`.
 * @param params.error - The failure being recovered from, kept for
 *   {@link getUnrecoveredChunkLoadError} and reported by
 *   {@link reportChunkLoadReload} after the reload. Omitted → a generic
 *   chunk-load `Error` is kept, and the report carries no message.
 * @param params.now - When the reload is requested, for tests. Omitted → the
 *   current time.
 * @returns True when a reload is pending, whether requested by this call or
 *   an earlier one; the caller should then neither show nor report the
 *   failure, since the page is about to unload.
 * @throws Never.
 */
export function reloadForChunkLoadError({
  buildCommit = BUILD_COMMIT,
  path = window.location.pathname,
  error,
  now = new Date(),
}: {
  buildCommit?: string;
  path?: string;
  error?: unknown;
  now?: Date;
} = {}): boolean {
  if (isReloadPending) {
    return true;
  }
  if (!shouldReloadForStaleChunk({buildCommit, path})) {
    return false;
  }
  unrecoveredChunkLoadError =
    error instanceof Error ? error : new Error('A chunk failed to load');
  writeReloadReport({
    fromBuild: buildCommit,
    path,
    error:
      error instanceof Error
        ? error.message.slice(0, MAX_REPORTED_ERROR_LENGTH)
        : '',
    reloadedAt: now.toISOString(),
  });
  setReloadPending(true);
  // A page with unsaved work makes the browser ask before unloading, and no
  // event reports the user declining. Registered last, so it sees what the
  // page's own listeners asked for.
  window.addEventListener(
    'beforeunload',
    event => {
      const isConfirmationRequested =
        event.defaultPrevented ||
        (typeof event.returnValue === 'string' && event.returnValue !== '');
      if (!isConfirmationRequested) {
        return;
      }
      setTimeout(() => {
        setReloadPending(false);
        // Declined, so no reload follows for the next load to report.
        removeReloadReport();
      }, CONFIRMED_RELOAD_GRACE_MS);
    },
    {once: true},
  );
  reloadPage({reason: 'stale-chunk'});
  return true;
}

/**
 * Tells, without recording anything, whether {@link reloadForChunkLoadError}
 * would return true — so a render can show the pending reload before an
 * effect requests it.
 *
 * @param params.buildCommit - As for {@link reloadForChunkLoadError}.
 * @param params.path - As for {@link reloadForChunkLoadError}.
 * @returns True when a reload is pending or would be allowed.
 * @throws Never.
 */
export function canReloadForChunkLoadError({
  buildCommit = BUILD_COMMIT,
  path = window.location.pathname,
}: {buildCommit?: string; path?: string} = {}): boolean {
  if (isReloadPending) {
    return true;
  }
  return (
    canStoreReloadMarker() && readReloadMarker() !== `${buildCommit} ${path}`
  );
}

/**
 * @returns True once {@link reloadForChunkLoadError} has requested a reload
 *   that has neither landed nor been declined by the user; see
 *   `CONFIRMED_RELOAD_GRACE_MS`.
 * @throws Never.
 */
export function isChunkLoadReloadPending(): boolean {
  return isReloadPending;
}

/**
 * Gives the chunk-load failure a recovery reload was requested for in this
 * document, if any. Once the reload is declined, every later error in the
 * document may stem from the import that never resolved, so error handling
 * should treat it as this failure.
 *
 * @returns The failure passed to {@link reloadForChunkLoadError}, or
 *   undefined when this document has not requested a recovery reload.
 * @throws Never.
 */
export function getUnrecoveredChunkLoadError(): Error | undefined {
  return unrecoveredChunkLoadError;
}

/**
 * Subscribes to changes of {@link isChunkLoadReloadPending}, in the shape
 * React's `useSyncExternalStore` expects.
 *
 * @param onChange - Called after every change.
 * @returns A function that removes the subscription.
 * @throws Never.
 */
export function subscribeToChunkLoadReloadPending(
  onChange: () => void,
): () => void {
  reloadPendingListeners.add(onChange);
  return () => {
    reloadPendingListeners.delete(onChange);
  };
}

/**
 * Reports, as a warning, the recovery reload that loaded this document, if
 * one did. Call it once per page load, after production logging is installed:
 * the warning is how guard reloads reach Cloud Logging. The report is taken
 * from storage synchronously, so a later call, or a later load, reports
 * nothing, even while the warning waits for `whenReadyToLog`.
 *
 * Best effort: a reload the user had to confirm is reported only when it
 * lands within `CONFIRMED_RELOAD_GRACE_MS` of the prompt, and a declined
 * reload followed by a manual one inside that grace is reported as a guard
 * reload.
 *
 * The warning carries `isSameBuild`: false when a release replaced the build
 * whose chunk failed, true when the chunk failed on the build that is still
 * live (a network failure), and null when either build is unknown.
 *
 * @param params.buildCommit - Build this document is running, for tests.
 *   Omitted → {@link BUILD_COMMIT}.
 * @param params.whenReadyToLog - Settles when the warning should be sent, so
 *   that the log entry can carry the signed-in user. A rejection sends it
 *   anyway. Omitted → the warning is sent synchronously.
 * @returns Nothing.
 * @throws Never — a `sessionStorage` failure or an unreadable report is
 *   logged at debug level and reports nothing.
 */
export function reportChunkLoadReload({
  buildCommit = BUILD_COMMIT,
  whenReadyToLog,
}: {buildCommit?: string; whenReadyToLog?: Promise<unknown>} = {}): void {
  const report = takeReloadReport();
  if (!report) {
    return;
  }
  const isBuildKnown =
    report.fromBuild !== UNKNOWN_BUILD_COMMIT &&
    buildCommit !== UNKNOWN_BUILD_COMMIT;
  const warn = () => {
    console.warn('[staleChunkGuard] reloaded after a failed chunk load', {
      ...report,
      toBuild: buildCommit,
      isSameBuild: isBuildKnown ? report.fromBuild === buildCommit : null,
    });
  };
  if (!whenReadyToLog) {
    warn();
    return;
  }
  void whenReadyToLog.then(warn, warn);
}

/**
 * Installs the `vite:preloadError` listener that recovers from a failed
 * chunk load.
 *
 * When no reload happens, the event is left to propagate so the failure
 * still reaches the error boundary and error reporting.
 *
 * @returns Nothing.
 * @throws Never.
 */
export function installStaleChunkGuard(): void {
  window.addEventListener('vite:preloadError', event => {
    if (!reloadForChunkLoadError({error: event.payload})) {
      return;
    }
    // Stops Vite rethrowing. Its import then resolves to `undefined`, and the
    // error that causes downstream is shown as loading by the error boundary.
    event.preventDefault();
  });
}
